import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import ProfileModal from '../ProfileModal';

export default function Header({ onMenuClick }) {
  const { user, logout, switchSchool } = useAuth();
  const navigate = useNavigate();
  const [showProfile, setShowProfile] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const isSuper = localStorage.getItem('is_super_admin') === 'true';
  const activeSchoolId = localStorage.getItem('active_school_id');
  const activeSchoolName = localStorage.getItem('active_school_name') || '';

  const showSwitchButton = isSuper && !!activeSchoolId;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleProfileUpdate = () => {
    window.location.reload();
  };

  const fetchUnread = async () => {
    try {
      const res = await api.get('/announcements/unread-count');
      setUnreadCount(res.data?.unread || 0);
    } catch {
      /* silent */
    }
  };

  useEffect(() => {
    if (!user) return;
    fetchUnread();
    const id = setInterval(fetchUnread, 60_000);
    return () => clearInterval(id);
  }, [user]);

  useEffect(() => {
    const onFocus = () => fetchUnread();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  return (
    <>
      <header className="bg-white shadow-sm p-3 sm:p-4 flex justify-between items-center flex-shrink-0">
        {/* Left: user info */}
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <button
            onClick={onMenuClick}
            className="lg:hidden text-gray-600 hover:text-blue-600 p-1 -ml-1"
            aria-label="Open menu"
          >
            <i className="fas fa-bars text-lg"></i>
          </button>

          <span className="font-semibold truncate">
            {user?.first_name} {user?.last_name}
          </span>
          <span className="text-xs bg-gray-200 px-2 py-1 rounded-full whitespace-nowrap hidden sm:inline capitalize">
            {user?.role?.replace(/_/g, ' ')}
          </span>

          {showSwitchButton && (
            <span className="hidden md:inline-flex items-center gap-1.5 text-xs bg-amber-100 text-amber-800 border border-amber-200 px-2.5 py-1 rounded-full whitespace-nowrap">
              <i className="fas fa-school"></i>
              {activeSchoolName}
            </span>
          )}
        </div>

        {/* Right: actions */}
        <div className="flex items-center gap-1 sm:gap-2">
          {showSwitchButton && (
            <button
              onClick={switchSchool}
              className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 px-3 py-1.5 rounded-lg transition"
              title="Switch to another school"
            >
              <i className="fas fa-exchange-alt"></i>
              <span>Switch School</span>
            </button>
          )}

          <button
            onClick={() => navigate('/dashboard/notifications')}
            className="relative text-gray-500 hover:text-blue-600 p-2 rounded-full hover:bg-gray-100 transition"
            title="Notifications"
          >
            <i className="fas fa-bell"></i>
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 leading-none">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>

          <button
            onClick={() => setShowProfile(true)}
            className="text-gray-500 hover:text-blue-600 p-2 rounded-full hover:bg-gray-100 transition"
            title="Profile & Settings"
          >
            <i className="fas fa-user-cog"></i>
          </button>

          <button
            onClick={handleLogout}
            className="text-gray-500 hover:text-red-500 p-2 rounded-full hover:bg-red-50 transition flex items-center gap-1"
            title="Logout"
          >
            <i className="fas fa-sign-out-alt"></i>
            <span className="hidden sm:inline text-sm">Logout</span>
          </button>
        </div>
      </header>

      <ProfileModal
        isOpen={showProfile}
        onClose={() => setShowProfile(false)}
        user={user}
        onUpdate={handleProfileUpdate}
      />
    </>
  );
}